import {createContext, memo, useMemo} from 'react'

import type {ColumnDef} from '../../../../hooks/tanstack-table/types'
import type {ColumnModel} from '../../../../models/column-model'
import {useAllColumns} from '../../../../state-providers/columns/use-all-columns'
import {useColumnWidth} from '../../../../state-providers/columns/use-column-width'
import {AddColumnHeader} from '../../add-column-header'
import {ExpandCell} from '../../cells/expand-cell'
import {getColumnBehaviors} from '../../column-behaviors'
import {AddColumnId, RoadmapPillAreaColumnId, RowDragHandleColumnId, RowExpandColumnId} from '../../column-ids'
import RowDragger, {ROW_DRAG_HANDLE_WIDTH} from '../../row-dragger'
import type {TableDataType} from '../../table-data-type'
import type {CellRenderProps} from './types'

type TableColumns = {
  /** Column definitions for use with @tanstack/react-table */
  columnDefinitions: Array<ColumnDef<TableDataType>>

  /**
   * Record of column visibility used by tanstack/react-table [docs](https://tanstack.com/table/v8/docs/api/features/column-visibility)
   */
  columnVisibility: Record<string, boolean>
}

export const TableColumnsContext = createContext<TableColumns | null>(null)

export const TableColumnsProvider = memo<{
  children: React.ReactNode
  isFieldVisible: (column: ColumnModel) => boolean
}>(function TableColumnsProvider({isFieldVisible, children}) {
  const {allColumns} = useAllColumns()
  const {getWidth} = useColumnWidth()

  const columnDefinitions: Array<ColumnDef<TableDataType>> = useMemo(() => {
    return allColumns.map(column => buildColumnDefs(column, getWidth(column.id)))
  }, [allColumns, getWidth])

  // For the roadmap view, "VisibleFieldsProvider" doesn't currently control the columns shown in a view.
  const columnVisibility = useMemo(() => {
    return allColumns.reduce(
      (acc, col) => {
        acc[col.id.toString()] = isFieldVisible(col)
        return acc
      },
      {} as Record<string, boolean>,
    )
  }, [allColumns, isFieldVisible])

  const contextValue = useMemo(() => {
    return {columnDefinitions, columnVisibility}
  }, [columnDefinitions, columnVisibility])

  return <TableColumnsContext.Provider value={contextValue}>{children}</TableColumnsContext.Provider>
})

function buildColumnDefs(column: ColumnModel, width: number | undefined): ColumnDef<TableDataType> {
  const columnMeta = getColumnBehaviors(column)
  return {
    header: column.name,
    id: column.id.toString(),
    accessorKey: column.id.toString(),
    size: width,
    meta: {
      columnModel: column,
      nonNavigable: false,
      typeToEditEnabled: true,
      ...columnMeta,
    },
  }
}

/**
 * Non-field Tanstack table column definition for the left-side numbered, row drag handle column
 */
export const RowDraggerColumnDef: ColumnDef<TableDataType> = {
  id: RowDragHandleColumnId,
  accessorKey: RowDragHandleColumnId,
  size: ROW_DRAG_HANDLE_WIDTH,
  minSize: ROW_DRAG_HANDLE_WIDTH,
  enableResizing: false,
  meta: {
    columnModel: undefined,
    Cell: (props: CellRenderProps) => {
      return <RowDragger row={props.row} />
    },
    nonNavigable: true,
    typeToEditEnabled: false,
  },
}

/**
 * Non-field Tanstack table column definition for the left-side numbered, expand/collapse row button
 */
export const RowExpandColumnDef: ColumnDef<TableDataType> = {
  id: RowExpandColumnId,
  accessorKey: RowExpandColumnId,
  size: 32,
  minSize: 32,
  enableResizing: false,
  meta: {
    columnModel: undefined,
    Cell: (props: CellRenderProps) => {
      return <ExpandCell row={props.row} />
    },
    nonNavigable: true,
    typeToEditEnabled: false,
  },
}

/**
 * Non-field Tanstack table column definition for the right-side "[+] Add field" header button
 */
export const AddColumnHeaderColumnDef: ColumnDef<TableDataType> = {
  header: AddColumnHeader,
  id: AddColumnId,
  accessorKey: AddColumnId,
  size: 50,
  minSize: 50,
  enableResizing: false,
  meta: {
    columnModel: undefined,
    Cell: () => null,
    nonNavigable: true,
    typeToEditEnabled: false,
  },
}

/**
 * Non-field Tanstack table column definition for the right-side pill area in the Roadmap view
 */
export const RoadmapPillAreaColumnDef: ColumnDef<TableDataType> = {
  id: RoadmapPillAreaColumnId,
  accessorKey: RoadmapPillAreaColumnId,
  size: 50,
  minSize: 50,
  enableResizing: false,
  meta: {
    columnModel: undefined,
    Cell: () => null,
    nonNavigable: false,
    typeToEditEnabled: false,
  },
}

try{ TableColumnsContext.displayName ||= 'TableColumnsContext' } catch {}
try{ TableColumnsProvider.displayName ||= 'TableColumnsProvider' } catch {}